import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Select from 'react-select';
import { DadosProfissionalProps, Professional, County } from './types';
import { IconContact, IconSearch, IconClose, IconAdvancePagination, IconBackPagination, IconUser } from '../../icons';
import api from '../../../services/api';
import { Oval } from 'react-loader-spinner';
import { comum } from '../../../styles/Themes/light';
import ModalDefault from '../../ModalDefault';
import {
  Container,
  SearchHeader,
  ProfessionalTitles,
  ProfessionalPrices,
  Paginate,
  EmptyState,
  ErrorMessage,
  ModalContactHead,
  ModalcontactMain
} from './styles';
import { HeaderContent, StepHeader, StepSubtitle, StepTitle } from '../styles';
import { selectTheme, selectComponents } from '../StepServico/InformacoesBasicas/styles';

const countySelectStyles = {
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    minHeight: 48,
    boxShadow: 'none',
    cursor: 'pointer',
    border: '1px solid var(--ce2e8f0)',
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'var(--c353a40)',
    fontSize: '16px',
  }),
  option: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'var(--c5a646e)',
    fontSize: '16px',
    fontWeight: '500',
  }),
  menu: (provided: Record<string, unknown>) => ({ ...provided, zIndex: 10 }),
};

function tipoParaCategoriaServico(tipo: string | undefined): string | null {
  if (!tipo) return null;
  if (tipo === "Audiência") return "Audiencia";
  if (tipo === "Diligência") return "Diligencia";
  if (tipo === "Protocolo") return "Protocolo";
  if (tipo === "Outros" || tipo === "Petição") return "Outros";
  return "Outros";
}

const DadosProfissional: React.FC<DadosProfissionalProps> = ({
  step,
  currentStep,
  setCurrentStep,
  validando,
  setValidando,
  setProfissional,
  comarca,
  dadosServico,
  tipoDemanda,
  tituloDemanda,
  idServicoSelecionado,
}) => {
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCounty, setSelectedCounty] = useState<string>(comarca || 'TodasTodas');
  const [selectedCountyId, setSelectedCountyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingValues, setLoadingValues] = useState(false);
  const [valuesStabilized, setValuesStabilized] = useState(true);
  const [loadingCounties, setLoadingCounties] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counties, setCounties] = useState<County[]>([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0
  });
  const [modalContact, setModalContact] = useState<boolean>(false);
  const [userContacts, setUserContacts] = useState({
    email: '',
    celular: '',
    tel_fixo: '',
    tel_recado: '',
  });
  const userInteracted = useRef(false);

  // Função para verificar se todos os valores estão prontos e estáveis
  const areAllValuesReady = () => {
    if (!professionals || professionals.length === 0) return false;
    
    // Se não estamos usando o endpoint com valores, considerar como pronto
    const serviceInfo = getServiceInfo();
    const isAllCounties = selectedCounty === 'TodasTodas' || selectedCounty === '';
    const idToUse = isAllCounties ? null : (selectedCountyId || (!userInteracted.current ? dadosServico?.dadosBasicos?.id_comarca : null));
    const hasRequiredData = shouldUseValoresEndpoint(serviceInfo, isAllCounties, idToUse);
    
    if (!hasRequiredData) {
      return true; // Se não precisa de valores, considerar pronto
    }
    
    const allReady = professionals.every(professional => {
      // Verifica se tem valor profissional válido
      const hasValidValue = typeof professional.valor_profissional === 'number' && 
                           isFinite(professional.valor_profissional) && 
                           professional.valor_profissional >= 0;
      
      // Verifica se tem lucro válido (pode ser negativo, mas deve ser um número finito)
      const hasValidProfit = typeof professional.lucro_porcentagem === 'number' && 
                            isFinite(professional.lucro_porcentagem);
      
      return hasValidValue && hasValidProfit;
    });
    
    return allReady;
  };

  // Profissionais que aparecem em todas as comarcas (mesmo array do SearchProfessionalProposal)
  const selectedPros = [
    3513, 3586, 3588, 3633, 3591, 3592, 3593, 3594, 3595, 3596, 3597, 3598,
    3618, 3619, 3620, 3621, 3622, 3669, 3699, 3700
  ];

  // Função para determinar categoria de serviço e ID do serviço
  const getServiceInfo = () => {
    if (idServicoSelecionado != null && idServicoSelecionado > 0) {
      return {
        categoria_servico: tipoParaCategoriaServico(tipoDemanda),
        id_servico: idServicoSelecionado,
      };
    }
    if (!tipoDemanda || !tituloDemanda) {
      return { categoria_servico: null, id_servico: null };
    }

    if (tipoDemanda === 'Audiência') {
      const modalidade = dadosServico?.audiencia?.modalidade;
      const isPresencial = modalidade === 'presencial' || modalidade === 'Presencial';
      
      let id_servico = null;
      switch (tituloDemanda) {
        case 'Audiência Cível':
          id_servico = isPresencial ? 1 : 17;
          break;
        case 'Audiência Penal':
          id_servico = isPresencial ? 3 : 19;
          break;
        case 'Audiência Trabalhista':
          id_servico = isPresencial ? 2 : 18;
          break;
        case 'Audiências':
          id_servico = isPresencial ? 4 : 20;
          break;
      }
      
      return { categoria_servico: 'Audiencia', id_servico };
    }

    if (tipoDemanda === 'Diligência') {
      let id_servico = null;
      switch (tituloDemanda) {
        case 'Acompanhamento de atos oficiais':
          id_servico = 5; break;
        case 'Comparecimento em cartório':
          id_servico = 6; break;
        case 'Cópia integral':
          id_servico = 8; break;
        case 'Cópia parcial':
          id_servico = 9; break;
        case 'Despacho':
          id_servico = 10; break;
        case 'Retirada de ofício e alvará':
          id_servico = 11; break;
        case 'Diligência':
          id_servico = 12; break;
        default:
          id_servico = 12; // fallback seguro para Diligência
      }
      return { categoria_servico: 'Diligencia', id_servico };
    }

    if (tipoDemanda === 'Protocolo') {
      let id_servico = null;
      switch (tituloDemanda) {
        case 'Acautelamento':
          id_servico = 13; break;
        case 'Protocolo eletrônico':
          id_servico = 14; break;
        case 'Protocolo físico':
          id_servico = 15; break;
        case 'Protocolo postal':
          id_servico = 16; break;
      }
      return { categoria_servico: 'Protocolo', id_servico };
    }

    // Demais serviços entram como "Outros"
    let id_servico = null;
    switch (tituloDemanda) {
      case 'Petições':
        id_servico = 22; break;
      case 'Outro serviço':
        id_servico = 21; break;
      case 'Localização de Veículo':
        id_servico = 23; break;
      default:
        id_servico = null;
    }
    return { categoria_servico: 'Outros', id_servico };
  };

  // Define se devemos usar o endpoint que retorna os valores dos profissionais
  // Regras:
  // - Sempre exige comarca específica e valor do cliente
  // - Em Audiência, também exige o campo profissional_necessario
  // - Em Diligência/Protocolo não exige profissional_necessario
  const shouldUseValoresEndpoint = (
    serviceInfo: { categoria_servico: string | null; id_servico: number | null },
    isAllCounties: boolean,
    idToUse: number | null | undefined
  ) => {
    if (isAllCounties) return false;
    const hasBase = Boolean(
      serviceInfo.categoria_servico &&
      serviceInfo.id_servico &&
      idToUse &&
      dadosServico?.dadosBasicos?.valor_cliente
    );

    if (!hasBase) return false;

    if (serviceInfo.categoria_servico === 'Audiencia') {
      return Boolean(dadosServico?.audiencia?.profissional_necessario);
    }

    // Diligencia / Protocolo
    return true;
  };

  const extractCityFromCounty = (value: string) => {
    if (!value) {
      return '';
    }

    const separatorIndex = value.indexOf(' - ');
    return separatorIndex >= 0 ? value.slice(separatorIndex + 3).trim() : value.trim();
  };

  const buildQueryParams = (pageNumber: number, name: string, includeGeneral = false, useValoresEndpoint = false) => {
    const params = new URLSearchParams();
    params.set('page', String(pageNumber));
    params.set('limit', '10');
    params.set('nome', name);
    params.set('status', 'ativo');
    params.set('ordem', 'usuario.nome');
    params.set('tipo_ordem', 'asc');
    
    if (includeGeneral) {
      params.set('profissionais_gerais', selectedPros.join(','));
    }
    
    // Se estamos usando o endpoint usuario-com-valores, adicionar os parâmetros necessários
      if (useValoresEndpoint) {
        const serviceInfo = getServiceInfo();
        
        if (serviceInfo.categoria_servico) {
          params.set('categoria_servico', serviceInfo.categoria_servico);
        }
        
        if (serviceInfo.id_servico) {
          params.set('id_servico', String(serviceInfo.id_servico));
        }
        
        if (dadosServico?.audiencia?.profissional_necessario) {
          params.set('profissional_necessario', dadosServico.audiencia.profissional_necessario);
        }
        
        const idToUse = selectedCounty === 'TodasTodas' ? null : (selectedCountyId || (!userInteracted.current ? dadosServico?.dadosBasicos?.id_comarca : null));
        if (idToUse) {
          params.set('id_comarca', String(idToUse));
        }
        
        if (dadosServico?.dadosBasicos?.valor_cliente) {
          params.set('valor_cliente', String(dadosServico.dadosBasicos.valor_cliente));
        }
        
      }

    return params;
  };

  // Função para processar e limpar os dados dos profissionais
  const processProfessionalsData = (professionals: Professional[]): Professional[] => {
    const processed = professionals.map(professional => {
      return {
        ...professional,
        nome: professional.nome.trim()
      };
    });
    return processed;
  };

  const getProfessionals = useCallback(async (city: string, page: number) => {
    try {
      setLoading(true);
      setError(null);

      const serviceInfo = getServiceInfo();
      const isAllCounties = selectedCounty === 'TodasTodas' || selectedCounty === '';
      const idToUse = isAllCounties ? null : (selectedCountyId || (!userInteracted.current ? dadosServico?.dadosBasicos?.id_comarca : null));
      const hasRequiredData = shouldUseValoresEndpoint(serviceInfo, isAllCounties, idToUse);

      // Se precisamos carregar valores, ativar loading específico para valores
      if (hasRequiredData) {
        setLoadingValues(true);
        setValuesStabilized(false);
      }

      const params = buildQueryParams(page, debouncedSearchTerm, false, hasRequiredData);
      const normalizedCounty = extractCityFromCounty(city);
      const selectedForFilter = normalizedCounty || debouncedSearchTerm;

      // Para o endpoint usuario-com-valores, não precisamos do filtro comarca pois ele já filtra por id_comarca
      // Para o endpoint usuario comum, usamos o filtro comarca
      if (!hasRequiredData && selectedForFilter) {
        params.set('comarca', selectedForFilter);
      }


      const endpoint = hasRequiredData ? 'usuario-com-valores' : 'usuario';
      const response = await api.get(`${endpoint}?${params.toString()}`);

      if (response.data.status !== 'success') {
        throw new Error('Erro ao carregar profissionais');
      }

      
      const processedProfessionals = processProfessionalsData(response.data.result.data);
      

      setProfessionals(processedProfessionals);
      setPagination(response.data.result.pagination);

      // Desativar loading de valores se estava ativo
      if (hasRequiredData) {
        setLoadingValues(false);
        // Verificar se todos os valores estão prontos antes de estabilizar
        let attempts = 0;
        const maxAttempts = 10; // Máximo 10 tentativas (1 segundo)
        
        const checkValuesStability = () => {
          attempts++;
          
          if (areAllValuesReady()) {
            setValuesStabilized(true);
          } else if (attempts < maxAttempts) {
            // Se ainda não estão prontos e não excedeu o limite, verificar novamente
            setTimeout(checkValuesStability, 100);
          } else {
            // Se excedeu o limite de tentativas, estabilizar mesmo assim para evitar loading infinito
            setValuesStabilized(true);
          }
        };
        
        // Iniciar verificação após um pequeno delay
        setTimeout(checkValuesStability, 100);
      } else {
        setValuesStabilized(true);
      }
      setLoading(false);
    } catch (err: any) {
      setError('Erro ao carregar profissionais');
      setLoadingValues(false);
      setValuesStabilized(true);
      setLoading(false);
    }
  }, [selectedCounty, selectedCountyId, debouncedSearchTerm, dadosServico]);

  const getAllProfessionals = useCallback(async (page: number) => {
    try {
      setLoading(true);
      setError(null);

      const serviceInfo = getServiceInfo();
      const isAllCounties = selectedCounty === 'TodasTodas' || selectedCounty === '';
      const idToUse = isAllCounties ? null : (selectedCountyId || (!userInteracted.current ? dadosServico?.dadosBasicos?.id_comarca : null));
      const hasRequiredData = shouldUseValoresEndpoint(serviceInfo, isAllCounties, idToUse);

      // Se precisamos carregar valores, ativar loading específico para valores
      if (hasRequiredData) {
        setLoadingValues(true);
        setValuesStabilized(false);
      }

      const params = buildQueryParams(page, debouncedSearchTerm, true, hasRequiredData);

      
      // Para getAllProfessionals, sempre incluímos profissionais gerais
      // O endpoint usuario-com-valores já filtra por id_comarca quando disponível

      const endpoint = hasRequiredData ? 'usuario-com-valores' : 'usuario';
      const response = await api.get(`${endpoint}?${params.toString()}`);

      if (response.data.status !== 'success') {
        throw new Error('Erro ao carregar profissionais');
      }

      
      const processedProfessionals = processProfessionalsData(response.data.result.data);
      

      setProfessionals(processedProfessionals);
      setPagination(response.data.result.pagination);
      
      // Desativar loading de valores se estava ativo
      if (hasRequiredData) {
        setLoadingValues(false);
      }
      setLoading(false);
    } catch (err: any) {
      setError('Erro ao carregar profissionais');
      setLoadingValues(false);
      setValuesStabilized(true);
      setLoading(false);
    }
  }, [selectedCounty, selectedCountyId, debouncedSearchTerm, dadosServico]);

  useEffect(() => {
    if (userInteracted.current) return;

    if (counties.length > 0) {
      let county = null;
      
      if (comarca) {
        const normalizedComarca = comarca.trim();
        county = counties.find(c => {
          const normalizedValue = c.value.trim();
          return normalizedValue === normalizedComarca || 
                 (normalizedComarca !== 'TodasTodas' && normalizedValue.toLowerCase() === normalizedComarca.toLowerCase());
        });
      }
      
      if (!county && dadosServico?.dadosBasicos?.id_comarca) {
        const idComarca = dadosServico.dadosBasicos.id_comarca;
        county = counties.find(c => c.id_comarca === idComarca);
      }
      
      if (county) {
        setSelectedCounty(county.value);
        setSelectedCountyId(county.id_comarca ?? null);
      } else if (comarca) {
        setSelectedCounty(comarca);
      } else if (!loadingCounties) {
        setSelectedCounty('TodasTodas');
        setSelectedCountyId(null);
      }
    } else if (comarca) {
      setSelectedCounty(comarca);
    }
  }, [comarca, counties, dadosServico?.dadosBasicos?.id_comarca, loadingCounties]);

  // Debounce para pesquisa automática
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Carregar comarcas do estado selecionado no Step 1
  const estado = dadosServico?.dadosBasicos?.estado;
  useEffect(() => {
    const getCounties = async () => {
      const countyArray: County[] = [
        { label: 'Todas as Comarcas', value: 'TodasTodas' }
      ];

      if (!estado || estado.trim() === '') {
        setCounties(countyArray);
        setLoadingCounties(false);
        return;
      }

      try {
        setLoadingCounties(true);
        const response = await api.get(`comarca?uf=${encodeURIComponent(estado)}`);

        if (response.data.status !== 'success') {
          throw new Error('Erro ao carregar comarcas');
        }

        (response.data.result || []).forEach((row: { uf?: string; cidade?: string; id_comarca?: number }) => {
          countyArray.push({
            label: `${row.uf || ''} - ${row.cidade || ''}`,
            value: `${row.uf || ''} - ${row.cidade || ''}`,
            id_comarca: row.id_comarca
          });
        });

        setCounties(countyArray);
      } catch (err) {
        setError('Erro ao carregar comarcas');
        setCounties(countyArray);
      } finally {
        setLoadingCounties(false);
      }
    };

    getCounties();
  }, [estado]);

  // Carregar profissionais
  useEffect(() => {
    // Só carrega profissionais se as comarcas já foram carregadas
    if (counties.length > 0) {
      const loadProfessionals = async () => {
        if (selectedCounty === 'TodasTodas' || selectedCounty === '') {
          await getAllProfessionals(1);
        } else {
          await getProfessionals(selectedCounty, 1);
        }
      };
      loadProfessionals();
    }
  }, [debouncedSearchTerm, selectedCounty, counties, getAllProfessionals, getProfessionals]);

  // Recarregar profissionais quando os dados do serviço mudarem
  useEffect(() => {
    if (counties.length > 0 && dadosServico) {
      // Só recarrega se realmente mudaram dados relevantes para o cálculo de valores
      const shouldReload = dadosServico.dadosBasicos?.valor_cliente || 
                          dadosServico.dadosBasicos?.id_comarca ||
                          dadosServico.audiencia?.profissional_necessario;
      
      if (shouldReload) {
        
        // Debounce para evitar múltiplas requisições rápidas
        const timer = setTimeout(() => {
          const loadProfessionals = async () => {
            if (selectedCounty === 'TodasTodas' || selectedCounty === '') {
              await getAllProfessionals(1);
            } else {
              await getProfessionals(selectedCounty, 1);
            }
          };
          loadProfessionals();
        }, 300);
        
        return () => clearTimeout(timer);
      }
    }
  }, [dadosServico?.dadosBasicos?.valor_cliente, dadosServico?.dadosBasicos?.id_comarca, dadosServico?.audiencia?.profissional_necessario, tipoDemanda, tituloDemanda, getAllProfessionals, getProfessionals, selectedCounty]);

  const handleProfessionalClick = (id: number) => {
    const professional = professionals.find(p => p.id_usuario === id);
    
    
    setSelectedProfessional(id);
    setProfissional(id);
    setError(null);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleCountyChange = (value: string) => {
    userInteracted.current = true;
    setSelectedCounty(value);
    const county = counties.find(c => c.value === value);
    setSelectedCountyId(county?.id_comarca ?? null);
    setProfessionals([]);
    setPagination({ currentPage: 1, lastPage: 1, total: 0 });
  };

  const selectedCountyOption = useMemo(
    () => counties.find(c => c.value === selectedCounty) || null,
    [counties, selectedCounty]
  );

  const handlePageChange = (page: number) => {
    if (selectedCounty === 'TodasTodas' || selectedCounty === '') {
      getAllProfessionals(page);
    } else {
      getProfessionals(selectedCounty, page);
    }
  };

  const handleContactClick = (professional: Professional) => {
    setModalContact(true);
    setUserContacts({
      email: professional.email,
      celular: professional.celular.replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1'),
      tel_fixo: professional.tel_fixo ? professional.tel_fixo.replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1') : '',
      tel_recado: professional.tel_recado ? professional.tel_recado.replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1') : ''
    });
  };

  useEffect(() => {
    if(validando && step === currentStep) {
      setValidando(false)
      // Operador/Admin podem avançar sem selecionar profissional
      setCurrentStep(currentStep + 1);
    }
  }, [validando]);

  if (currentStep !== step) {
    return null;
  }

  return (
    <Container>
      <StepHeader>
        <HeaderContent>
          <StepTitle>Profissionais</StepTitle>
          <StepSubtitle>Selecione o profissional que deseja contratar.</StepSubtitle>
        </HeaderContent>
      </StepHeader>
      
      <SearchHeader>
        <div className="search">
          <div className="search-icon">
            <IconSearch />
          </div>
          <input
            type="text"
            placeholder="Pesquisar profissional..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <div className="clean-search" onClick={clearSearch}>
              <IconClose />
            </div>
          )}
        </div>
        
        <div className="countie-status">
          <span>{pagination.total} resultados para:</span>
          <div style={{ minWidth: 220 }}>
            <Select<County>
              value={selectedCountyOption}
              options={counties}
              onChange={(opt) => handleCountyChange(opt?.value ?? 'TodasTodas')}
              placeholder={loadingCounties ? 'Carregando...' : 'Selecionar comarca'}
              isDisabled={loadingCounties}
              isSearchable
              isClearable={false}
              styles={countySelectStyles}
              theme={selectTheme}
              components={selectComponents}
              getOptionValue={(opt) => opt.value}
              getOptionLabel={(opt) => opt.label}
            />
          </div>
        </div>
      </SearchHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ProfessionalTitles>
        <div className="icon-profile"></div>
        <div className="name">Nome</div>
        <div className="service">Serviço</div>
        <div className="profit">Lucro</div>
        <div className="code">Código</div>
        <div className="contact"></div>
      </ProfessionalTitles>

      {(loading || loadingValues || !valuesStabilized) ? (
        <div className="professionals-loading">
          <Oval
            height={80}
            width={80}
            color={comum.colors.primaryRed400}
            wrapperStyle={{}}
            wrapperClass="professionals-loader"
            visible={true}
            ariaLabel='oval-loading'
            secondaryColor={comum.colors.primaryRed400}
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
        </div>
      ) : (
        <>
          {professionals.length === 0 ? (
            <EmptyState>
              <div>Nenhum profissional encontrado</div>
            </EmptyState>
          ) : (
            professionals.map((professional) => (
              <ProfessionalPrices
                key={professional.id_usuario}
                selected={selectedProfessional === professional.id_usuario}
                onClick={() => handleProfessionalClick(professional.id_usuario)}
              >
                <div className="icon-profile">
                  <IconUser color={selectedProfessional === professional.id_usuario ? comum.colors.primaryRed400 : comum.colors.interfaceGray500} />
                </div>
                <div className="name">{professional.nome}</div>
                <div className="service">
                  {(() => {
                    // Se estiver em "Todas as Comarcas", não mostra valor específico
                    if (selectedCounty === 'TodasTodas' || selectedCounty === '') {
                      return '-';
                    }
                    
                    // Se os valores ainda não estão estabilizados, mostrar loading
                    if (!valuesStabilized) {
                      return '...';
                    }
                    
                    // Verificação adicional para garantir que o valor é válido
                    const valor = typeof professional.valor_profissional === 'number' && 
                                 isFinite(professional.valor_profissional) && 
                                 professional.valor_profissional >= 0
                      ? professional.valor_profissional
                      : null;
                    
                    // Se o valor ainda não é válido mesmo com valuesStabilized = true, mostrar loading
                    if (valor === null) {
                      return '...';
                    }
                    
                    const valorFormatado = `R$ ${valor},00`;
                    return valorFormatado;
                  })()}
                </div>
                <div className={`profit ${(() => {
                  const pct = typeof professional.lucro_porcentagem === 'number' && isFinite(professional.lucro_porcentagem)
                    ? professional.lucro_porcentagem
                    : 0;
                  return pct < 0 ? 'negative' : '';
                })()}`}>
                  {(() => {
                    // Se estiver em "Todas as Comarcas", não mostra lucro específico
                    if (selectedCounty === 'TodasTodas' || selectedCounty === '') {
                      return '-';
                    }
                    
                    // Se os valores ainda não estão estabilizados, mostrar loading
                    if (!valuesStabilized) {
                      return '...';
                    }
                    
                    // Verificação adicional para garantir que o valor é válido
                    const pct = typeof professional.lucro_porcentagem === 'number' && isFinite(professional.lucro_porcentagem)
                      ? professional.lucro_porcentagem
                      : null;
                    
                    // Se o valor ainda não é válido mesmo com valuesStabilized = true, mostrar loading
                    if (pct === null) {
                      return '...';
                    }
                    
                    const pctFormatado = `${pct.toFixed(2)}%`;
                    return pctFormatado;
                  })()}
                </div>
                <div className="code">D{professional.id_usuario}</div>
                <div 
                  className="contact"
                  onClick={(e) => {
                    handleContactClick(professional);
                  }}
                >
                  <IconContact color={comum.colors.primaryRed400} />
                </div>
              </ProfessionalPrices>
            ))
          )}

          {pagination.lastPage > 1 && (
            <Paginate>
              <div className="pagination">
                <button
                  className="back-page"
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage <= 1}
                >
                  <div className="back-link-page">
                    <IconBackPagination />
                  </div>
                </button>
                
                {(() => {
                  const totalPages = pagination.lastPage;
                  const currentPage = pagination.currentPage;
                  const maxVisiblePages = 5;
                  
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  // Ajustar startPage se estivermos no final
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }
                  
                  const pages = [];
                  
                  // Primeira página e reticências se necessário
                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        className="page-link"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="page-ellipsis">
                          ...
                        </span>
                      );
                    }
                  }
                  
                  // Páginas visíveis
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`page-link ${currentPage === i ? 'active' : ''}`}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  // Última página e reticências se necessário
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="page-ellipsis">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        className="page-link"
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  
                  return pages;
                })()}
                
                <button
                  className="next-page"
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.lastPage}
                >
                  <div className="next-link-page">
                    <IconAdvancePagination />
                  </div>
                </button>
              </div>
            </Paginate>
          )}
        </>
      )}

      <ModalDefault
        id={'overlayModal'}
        onClose={() => setModalContact(false)}
        openModal={modalContact}
        width="fit-content"
      >
        <ModalContactHead>
          <div>
            Entre em contato
          </div>
          <div
            className="close"
            onClick={() => setModalContact(false)}
          >
            <IconClose />
          </div>
        </ModalContactHead>
        
        <ModalcontactMain>
          <div>{userContacts.email}</div>
          <div>{userContacts.celular}</div>
          <div>{userContacts.tel_fixo ? userContacts.tel_fixo : 'sem telefone fixo'}</div>
          <div>{userContacts.tel_recado ? userContacts.tel_recado : 'sem telefone de recado'}</div>
        </ModalcontactMain>
      </ModalDefault>
    </Container>
  );
};

export default DadosProfissional;
